import { useEffect } from 'react';

import { overlays } from '@revenuemarket/overlays';

import { CompanyUserPermission, RoleDetailResponse } from '~apis/role/type';
import PermissionInfoModal from '~components/ui/Modal/PermissionInfoModal';
import { useUserPermissions } from '~hooks/queries/role/query';

/**
 * 권한 체크 컴포넌트
 * @param required 필요한 권한 목록 (회사 권한 또는 자산 권한)
 * @param fallback 권한이 없을 때 보여줄 컴포넌트
 * @param children 권한이 있을 때 보여줄 컴포넌트
 * @param showModal 권한 정보 모달 표시 여부
 * @param mode 권한 체크 모드 ('every': 모든 권한 필요, 'some': 하나라도 있으면 허가) - 기본값: 'every'
 * @returns
 */
export function PermissionGuard({
  required,
  fallback = null,
  children,
  showModal = false,
  mode = 'every',
}: {
  required: PermissionRequirement | PermissionRequirement[];
  fallback?: React.ReactNode;
  children: React.ReactNode;
  showModal?: boolean;
  mode?: PermissionCheckMode;
}) {
  const { hasPermission, isLoading } = usePermissionCheck(required, mode);

  useEffect(() => {
    if (!isLoading && !hasPermission && showModal) {
      overlays.open(({ isOpen, onClose }) => (
        <PermissionInfoModal
          open={isOpen}
          onCancel={onClose}
          type="screen"
          requiredPermission={Array.isArray(required) ? required : [required]}
        />
      ));
    }
  }, [hasPermission, showModal, required, isLoading]);

  if (isLoading) {
    return null; // 또는 로딩 스피너
  }

  return hasPermission ? children : fallback ?? null;
}

/**
 * 권한 체크 함수
 * @param required 필요한 권한 목록 (회사 권한 또는 자산 권한)
 * @param userPermissions 사용자 권한 목록
 * @param target 권한이 있을 때 보여줄 컴포넌트
 * @param fallback 권한이 없을 때 보여줄 컴포넌트
 * @param mode 권한 체크 모드 ('every': 모든 권한 필요, 'some': 하나라도 있으면 허가) - 기본값: 'every'
 * @returns 권한 체크 결과와 관련 정보
 */
export function permissionGuardFilter<T>({
  required,
  userPermissions,
  target,
  fallback = null,
  mode = 'every',
}: {
  required: string | string[];
  userPermissions: string[];
  target: T;
  fallback?: T | null;
  mode?: PermissionCheckMode;
}): T | null {
  const requiredList = Array.isArray(required) ? required : [required];
  const checkFunction = mode === 'some' ? 'some' : 'every';
  return requiredList[checkFunction]((r) => userPermissions.includes(r)) ? target : fallback;
}

// 권한 체크 모드 타입
export type PermissionCheckMode = 'every' | 'some';

// DataPermissions 인터페이스를 만족하는 속성들을 추출하는 유틸리티 타입
type DataPermissionKeys<T> = {
  [K in keyof T]: T[K] extends { allowAllView: boolean; allowAllEdit: boolean } ? K : never;
}[keyof T];

// 자산 권한 체크를 위한 타입 정의 (동적 확장 가능)
export type AssetPermissionType = DataPermissionKeys<RoleDetailResponse>;

// 자산 권한 요구 사항 타입
export interface AssetPermissionRequirement {
  type: AssetPermissionType;
  requireEdit?: boolean; // allowEdit을 체크할지 여부 (기본값: true)
}

// 통합 권한 요구 사항 타입
export type PermissionRequirement = CompanyUserPermission | AssetPermissionRequirement;

// 자산 권한을 체크하기 위한 헬퍼 함수
function checkAssetPermission(
  permissionData: RoleDetailResponse | undefined,
  requirement: AssetPermissionRequirement,
): boolean {
  if (!permissionData) return false;

  const assetPermissions = permissionData[requirement.type];
  if (!assetPermissions) return false;

  // requireEdit이 false인 경우 allowAllView만 체크
  if (requirement.requireEdit === false) {
    return assetPermissions.allowAllView;
  }

  // 기본적으로 allowEdit을 체크 (allowEdit이 true여야 권한이 있다고 판단)
  return assetPermissions.allowAllEdit;
}

/**
 * 권한 체크를 위한 커스텀 훅
 * @param required 필요한 권한 목록 (회사 권한 또는 자산 권한)
 * @param mode 권한 체크 모드 ('every': 모든 권한 필요, 'some': 하나라도 있으면 허가) - 기본값: 'every'
 * @returns 권한 체크 결과와 관련 정보
 */
export function usePermissionCheck(
  required: PermissionRequirement | PermissionRequirement[],
  mode: PermissionCheckMode = 'every',
) {
  const { data: permissionData, isLoading } = useUserPermissions();

  const requiredList = Array.isArray(required) ? required : [required];

  const hasPermission =
    !isLoading && permissionData?.Data?.data
      ? (() => {
          const checkFunction = mode === 'some' ? 'some' : 'every';
          return requiredList[checkFunction]((requirement) => {
            // 문자열인 경우 기존 CompanyUserPermission 체크
            if (typeof requirement === 'string') {
              const permissions = permissionData.Data?.data.companyUserPermissions;
              return permissions?.includes(requirement as CompanyUserPermission);
            }

            // 객체인 경우 자산 권한 체크
            return checkAssetPermission(permissionData.Data?.data, requirement);
          });
        })()
      : false;

  return {
    hasPermission,
    permissions: permissionData?.Data?.data?.companyUserPermissions,
    permissionData: permissionData?.Data?.data,
    isLoading,
    requiredPermissions: requiredList,
    mode,
  };
}
